import { useState, useEffect } from 'react';
import { getPageBanner } from '../services/api';

// Default banner images for each page (fallback when no CMS banner is set)
const DEFAULT_BANNERS = {
  churches: 'https://images.unsplash.com/photo-1465848059293-208e11dfea17',
  news: 'https://images.unsplash.com/photo-1609234656388-0ff363383899',
  events: 'https://images.unsplash.com/photo-1507692049790-de58290a4334',
  bible_readings: 'https://images.unsplash.com/photo-1529070538774-1843cb3265df',
  photo_gallery: 'https://images.unsplash.com/photo-1695938746747-ec185ea81325',
  contact: 'https://images.unsplash.com/photo-1628798294389-421de207d2b0',
};

/**
 * Hook to fetch page banner from CMS with fallback to default
 * @param {string} pageId - The page identifier (churches, news, events, etc.)
 * @param {string} language - Current language code
 * @returns {object} - { bannerImage, bannerTitle, bannerSubtitle, loading }
 */
const usePageBanner = (pageId, language = 'sv') => {
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBanner = async () => {
      try {
        const data = await getPageBanner(pageId);
        setBanner(data);
      } catch (error) {
        console.error(`Error loading banner for ${pageId}:`, error);
        setBanner(null);
      } finally {
        setLoading(false);
      }
    };

    loadBanner();
  }, [pageId]);

  // Get the appropriate content based on language
  const getBannerImage = () => {
    if (banner?.image_url && banner?.active) {
      return banner.image_url;
    }
    return DEFAULT_BANNERS[pageId] || DEFAULT_BANNERS.churches;
  };

  const getBannerTitle = () => {
    if (banner?.title && banner?.active) {
      return banner.title[language] || banner.title.sv || null;
    }
    return null;
  };

  const getBannerSubtitle = () => {
    if (banner?.subtitle && banner?.active) {
      return banner.subtitle[language] || banner.subtitle.sv || null;
    }
    return null;
  };

  return {
    bannerImage: getBannerImage(),
    bannerTitle: getBannerTitle(),
    bannerSubtitle: getBannerSubtitle(),
    hasCMSBanner: banner?.active === true,
    loading,
  };
};

export default usePageBanner;
